<template>
    <modal
        name="view-translations-modal"
        :adaptive="true"
        height="auto"
        width="600px"
        class="cursor-default"
        style="z-index: 9999"
        :clickToClose="false"
    >
        <div
            class="w-full transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all"
        >
            <div class="relative overflow-hidden bg-white">
                <div class="relative">
                    <span class="text-xl font-semibold text-gray-800">
                        {{ fieldName }} Translations
                    </span>
                    <div class="mt-4">
                        <div class="mb-4 flex items-center space-x-2">
                            <select
                                v-model="selectedLanguage"
                                class="form-select block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                                <option value="">Select a language to add...</option>
                                <option
                                    v-for="(name, code) in availableLanguages"
                                    :key="code"
                                    :value="code"
                                >
                                    {{ name }} ({{ code }})
                                </option>
                            </select>
                            <button
                                @click="addNewTranslation()"
                                :disabled="!selectedLanguage"
                                type="button"
                                class="focus:outline-none inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition duration-150 ease-in-out hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Add
                            </button>
                        </div>
                        <div
                            v-if="hasTranslations"
                            class="max-h-96 space-y-4 overflow-y-auto"
                        >
                            <div
                                v-for="(translation, lang) in editableTranslations"
                                :key="lang"
                                class="border-b border-gray-200 pb-3 last:border-b-0"
                            >
                                <div class="flex items-center justify-between">
                                    <span class="text-sm font-medium text-gray-700">
                                        {{ getLanguageName(lang) }} ({{ lang }})
                                    </span>
                                    <button
                                        @click="removeTranslation(lang)"
                                        type="button"
                                        class="text-red-400 hover:text-red-700"
                                        v-tooltip="'Remove translation'"
                                    >
                                        <svg
                                            class="h-5 w-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                            ></path>
                                        </svg>
                                    </button>
                                </div>
                                <div class="mt-2">
                                    <textarea
                                        v-if="fieldType === 'description'"
                                        v-model="editableTranslations[lang][fieldType]"
                                        :rows="3"
                                        class="form-input block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        :placeholder="`Enter ${fieldName.toLowerCase()} in ${getLanguageName(lang)}`"
                                    />
                                    <input
                                        v-else
                                        v-model="editableTranslations[lang][fieldType]"
                                        type="text"
                                        class="form-input block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        :placeholder="`Enter ${fieldName.toLowerCase()} in ${getLanguageName(lang)}`"
                                    />
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-sm text-gray-500">
                            No translations available for this field.
                        </div>
                    </div>
                </div>
            </div>
            <div class="mt-5 flex w-full justify-end space-x-3">
                <button
                    @click="hideModal()"
                    type="button"
                    class="focus:outline-none inline-flex w-auto justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium leading-6 text-gray-700 shadow-sm transition duration-150 ease-in-out hover:text-gray-500 focus:border-blue-300 focus:shadow-outline sm:text-sm sm:leading-5"
                >
                    Cancel
                </button>
                <button
                    @click="saveTranslations()"
                    type="button"
                    class="focus:outline-none inline-flex w-auto justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium leading-6 text-white shadow-sm transition duration-150 ease-in-out hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo sm:text-sm sm:leading-5"
                >
                    Save
                </button>
            </div>
        </div>
    </modal>
</template>

<script>
import { languageNames, getLanguageName } from '@/helpers/languageNames';

export default {
    name: 'ViewTranslationsModal',
    data() {
        return {
            translations: {},
            editableTranslations: {},
            fieldType: 'name',
            fieldName: 'Name',
            onSaveCallback: null,
            selectedLanguage: '',
            languageNames
        };
    },
    computed: {
        hasTranslations() {
            return Object.keys(this.editableTranslations).length > 0;
        },
        availableLanguages() {
            const allLanguages = { ...this.languageNames };
            // Remove languages that already have translations
            Object.keys(this.editableTranslations).forEach(lang => {
                delete allLanguages[lang];
            });
            return allLanguages;
        }
    },
    methods: {
        hideModal() {
            this.$modal.hide('view-translations-modal');
        },
        getLanguageName,
        showTranslations(translations, fieldType, fieldName, onSaveCallback) {
            this.translations = translations || {};
            this.fieldType = fieldType;
            this.fieldName = fieldName;
            this.onSaveCallback = onSaveCallback;
            // Create a deep copy of translations for editing
            this.editableTranslations = JSON.parse(JSON.stringify(this.translations));
        },
        saveTranslations() {
            if (this.onSaveCallback) {
                this.onSaveCallback(this.editableTranslations);
            }
            this.hideModal();
        },
        addNewTranslation() {
            if (!this.selectedLanguage) {
                return;
            }
            // Initialize the translation object for the selected language
            this.$set(this.editableTranslations, this.selectedLanguage, {
                name: '',
                description: ''
            });
            // Reset the selected language
            this.selectedLanguage = '';
        },
        removeTranslation(lang) {
            this.$delete(this.editableTranslations, lang);
        }
    }
};
</script>
